/**
 * Multi-publicação de atividades Clubs: uma Action no Clubs,
 * filhas selecionadas só ganham acesso (sem cópia).
 * Visível apenas no contexto da entidade/estação Clubs.
 * Hierarquia de tipos suportados (ordem de prioridade):
 * 1. Redirecionamento (13)
 * 2. Cupom de desconto (3)
 * 3. Post (16)
 * 4. Link externo / Redirecionamento interno (20)
 * 5. Link de pagamento (flag is_link_pagamento — fluxo próprio)
 * 6. Venda online parceiro (22 — módulo próprio)
 * 7. Formulário (8)
 */

export const CLUBS_STATION_ID = 174;
export const CLUBS_ESTABLISHMENT_ID = 229;

export const CLUBS_MULTI_STATION_TYPE_IDS = [13, 3, 16, 20, 8];

export const CLUBS_MULTI_STATION_HIERARCHY = [
  { key: 'redirecionamento', typeId: 13, label: 'Redirecionamento' },
  { key: 'cupom', typeId: 3, label: 'Cupom de desconto' },
  { key: 'post', typeId: 16, label: 'Post' },
  { key: 'link_externo', typeId: 20, label: 'Link externo' },
  { key: 'link_pagamento', typeId: 23, label: 'Link de pagamento', flag: 'is_link_pagamento' },
  { key: 'venda_online_parceiro', typeId: 22, label: 'Venda online parceiro' },
  { key: 'formulario', typeId: 8, label: 'Formulário' },
];

export const isClubsMultiStationType = (typeId) => {
  const id = parseInt(typeId, 10);
  return CLUBS_MULTI_STATION_TYPE_IDS.includes(id);
};

/** Station/establishment ativo no Lab (sempre establishments[0]). */
export const getActiveStationId = (auth) =>
  auth?.user?.establishments?.[0]?.stations?.[0]?.id || null;

export const getActiveEstablishmentId = (auth) => auth?.user?.establishments?.[0]?.id || null;

/** Feature liberada só no contexto Clubs. */
export const isClubsStationContext = (auth) => {
  const stationId = parseInt(getActiveStationId(auth), 10);
  const establishmentId = parseInt(getActiveEstablishmentId(auth), 10);
  return stationId === CLUBS_STATION_ID || establishmentId === CLUBS_ESTABLISHMENT_ID;
};

export const getUserStations = (auth) => {
  const establishments = auth?.user?.establishments || [];
  const stations = [];
  const seen = new Set();

  establishments.forEach((establishment) => {
    (establishment?.stations || []).forEach((station) => {
      if (station?.id && !seen.has(station.id)) {
        seen.add(station.id);
        stations.push(station);
      }
    });
  });

  return stations;
};

export const getDefaultStationId = (auth) => {
  return auth?.user?.establishments?.[0]?.stations?.[0]?.id || null;
};

/**
 * Atividade criada via multi-station a partir de uma Action pai.
 * Filhas não editam — só a entidade pai.
 */
export const isMultiStationChildAction = (action) => {
  const parentId = action?.parent_action_id;
  if (parentId == null || parentId === '') return false;
  const parsed = parseInt(parentId, 10);
  return !Number.isNaN(parsed) && parsed > 0;
};

export const CHILD_ACTION_EDIT_BLOCKED_MESSAGE =
  'Esta atividade foi publicada pela entidade pai e não pode ser editada aqui. Edite a atividade original na entidade pai para replicar nas estações.';

/**
 * Normaliza o valor da imagem do formulario pra chave S3 usada no Action.image
 * (ex: files/actions/stations/IMG_xxx).
 * InputEasyImage devolve um File { id, path, saved_name } — nao usar .id nem .path sozinho.
 */
export const toActionImageKey = (image) => {
  if (!image) return null;

  if (typeof image === 'string') {
    const trimmed = image.trim();
    if (!trimmed) return null;
    // ID numerico ou pasta sem arquivo nao sao URLs validas
    if (/^\d+$/.test(trimmed)) return null;
    if (trimmed === 'files/actions/stations' || trimmed === 'files/actions/admin') return null;
    return trimmed;
  }

  if (typeof image === 'object') {
    if (image.path && image.saved_name) {
      return `${image.path}/${image.saved_name}`;
    }
    if (typeof image.url === 'string') {
      return toActionImageKey(image.url);
    }
    // Objeto com base64 ainda nao enviado — deixa o fluxo legado tratar
    if (image.base64) {
      return null;
    }
  }

  return null;
};

/**
 * Resolve IDs de stations para publicação.
 * Se stations_field estiver vazio, usa a station padrão do usuário.
 */
export const resolveStationIds = (values, defaultStationId) => {
  const fromField = (values?.stations_field || [])
    .map((s) => (typeof s === 'object' ? s.id : s))
    .filter(Boolean);

  if (fromField.length > 0) {
    return [...new Set(fromField)];
  }

  if (values?.station) {
    return [values.station];
  }

  if (values?.station_id) {
    return [values.station_id];
  }

  return defaultStationId ? [defaultStationId] : [];
};

/**
 * IDs selecionados no campo (categorias/agrupamentos), sem filtrar por station.
 * Categorias do cadastro precisam ir para pai e filhas — senão a homepage da
 * station filha não encontra Social/EDUCAÇÃO (que são do Clubs).
 */
export const selectedFieldIds = (fieldItems) => {
  if (!Array.isArray(fieldItems) || fieldItems.length === 0) {
    return [];
  }

  return fieldItems
    .map((item) => (typeof item === 'object' && item != null ? item.id : item))
    .filter(Boolean);
};

/**
 * Itens do campo que pertencem à station (ou sem stationId definido).
 * Usado em agrupamentos, que são por station.
 */
export const filterIdsForStation = (fieldItems, stationId) => {
  if (!Array.isArray(fieldItems) || fieldItems.length === 0) {
    return [];
  }

  return fieldItems
    .filter((item) => {
      if (item == null) return false;
      if (typeof item !== 'object') return true;
      if (item.stationId == null && item.station_id == null) return true;
      return item.stationId === stationId || item.station_id === stationId;
    })
    .map((item) => (typeof item === 'object' ? item.id : item))
    .filter(Boolean);
};

/**
 * Campos necessários para criar/atualizar Action nas stations.
 * Evita reenviar o GET da activity inteiro (type, relations, body aninhado…),
 * que infla o POST e causa 502 no proxy Netlify (elasticbeanstalk).
 */
const MULTI_STATION_PAYLOAD_KEYS = [
  'name',
  'description',
  'summary',
  'body_summary',
  'rules',
  'has_rules',
  'start',
  'end',
  'participationStart',
  'participationEnd',
  'click_me_label',
  'limit',
  'limitPerUser',
  'usesLimitByAudience',
  'limitFiliado',
  'limitAmigo',
  'limitDependente',
  'audience_types',
  'points',
  'points_invited',
  'points_inviter',
  'type_action',
  'type_id',
  'has_target',
  'target',
  'from',
  'to',
  'states',
  'associated',
  'internal',
  'segmentation_type',
  'segment_users',
  'segment_plans',
  'entities',
  'featuredPublic',
  'featuredPrivate',
  'featuredImage',
  'hasPriority',
  'priority',
  'hasPriorityInCarousel',
  'priorityCarousel',
  'comments_and_likes_enabled',
  'only_public',
  'journey_only',
  'sponsored',
  'partner',
  'partner_id',
  'feedback_message',
  'feedback_lottie_id',
  'trophy_image',
  'trophy_description',
  'isThereTrophy',
  'has_giveaway',
  'giveaway_id',
  'giveaway_quantity',
  'giveaway_quantity_invited',
  'url',
  'app_uri',
  'sell_vouchers',
  'sell_vouchers_code',
  'date_validity_voucher',
  'payment_option',
  'is_draft',
  'active',
  'primaryImageId',
  'image_id',
];

/**
 * Extrai id numérico de video (campo do form pode ser number, string ou { id }).
 */
export const toVideoId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return parseInt(value.trim(), 10);
  }
  if (typeof value === 'object') {
    return toVideoId(value.video_id ?? value.id ?? value.value);
  }
  return null;
};

/**
 * Normaliza `videos` / `videoIdField` pro formato esperado pelo createAction
 * ({ video_id: number, name, description, required }).
 */
export const normalizeVideosPayload = (values) => {
  const fromVideos = Array.isArray(values?.videos) ? values.videos : [];
  let videos = fromVideos
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'number' || typeof item === 'string') {
        const video_id = toVideoId(item);
        if (video_id == null) return null;
        return { video_id, name: '', description: '', required: false };
      }
      const video_id = toVideoId(item.video_id ?? item.id ?? item);
      if (video_id == null) return null;
      return {
        video_id,
        name: item.name || '',
        description: item.description || '',
        required: Boolean(item.required),
      };
    })
    .filter(Boolean);

  if (videos.length === 0) {
    const video_id = toVideoId(values?.videoIdField);
    if (video_id != null) {
      videos = [{ video_id, name: '', description: '', required: false }];
    }
  }

  return videos;
};

export const buildStationPayload = (values, stationId) => {
  const stationImages = values.station_images || {};
  const rawStationImage = stationImages[stationId] || values.image;
  const imageKey = toActionImageKey(rawStationImage) || toActionImageKey(values.image);

  const categoriesFromField = selectedFieldIds(values.categories_field);
  const groupingsFromField = filterIdsForStation(values.groupings_field, stationId);

  const categories =
    categoriesFromField.length > 0
      ? categoriesFromField
      : Array.isArray(values.categories)
      ? values.categories
      : [];

  const groupings =
    groupingsFromField.length > 0
      ? groupingsFromField
      : Array.isArray(values.groupings)
      ? values.groupings
      : [];

  const payload = {};
  MULTI_STATION_PAYLOAD_KEYS.forEach((key) => {
    if (values[key] !== undefined) {
      payload[key] = values[key];
    }
  });

  payload.station = stationId;
  payload.station_id = stationId;
  payload.categories = categories;
  payload.groupings = groupings;
  payload.type_action = values.type_action || values.type_id;
  payload.image = imageKey || (typeof rawStationImage === 'string' ? rawStationImage : null);
  payload.primaryImageId = imageKey || values.primaryImageId;
  payload.summary = values.summary || values.body_summary;
  payload.body_summary = values.body_summary || values.summary;

  const videos = normalizeVideosPayload(values);
  if (videos.length > 0) {
    payload.videos = videos;
  } else {
    delete payload.videos;
  }

  const fileSource =
    (typeof rawStationImage === 'object' && rawStationImage) ||
    (typeof values.image === 'object' && values.image) ||
    null;
  if (fileSource?.id && imageKey) {
    payload.image_id = fileSource.id;
  }

  const needsUpload =
    !imageKey &&
    ((typeof rawStationImage === 'object' && rawStationImage?.base64) || values.base64);
  if (needsUpload) {
    payload.base64 =
      (typeof rawStationImage === 'object' && rawStationImage.base64) || values.base64;
    payload.imageType =
      (typeof rawStationImage === 'object' && rawStationImage.imageType) || values.imageType;
  }

  return payload;
};
